import type { FC } from 'react';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { CountryConfigNames } from '../../contexts/country-config-context/country-config-enums';
import { useGlobalModalLaunchContext } from '../../contexts/modal-context/global-modal-context';
import { BillingPlanIds } from '../../enums/billing-plan-ids';
import { useEnableUpgradePlanPromotionalBanner } from '../../feature-flags/hooks/use-enable-upgrade-plan-promotional-banner';
import { useCountryConfig } from '../../hooks/use-country-config';
import { useUserSpecificLocalStorage } from '../../hooks/use-local-storage';
import Global from '../../store/global-selectors';
import { PromotionalBanner } from '../promotional-banner';
import { messages } from './messages';

import type { TextValue } from 'ui-components/components/text/text-types';

type UpgradePlanPromotionalBannerProps = {
  bannerId?: string;
  className?: string;
  showCloseButton?: boolean;
  promotionalHeaderMessage?: TextValue;
  promotionalSubheaderMessage?: TextValue;
};

/**
 * A promotional banner component that encourages users to upgrade to a paid plan
 * to unlock improved wallet rates. Displays information about the benefits of upgrading
 * and provides an upgrade button that opens the manage subscription drawer.
 */
export const UpgradePlanPromotionalBanner: FC<UpgradePlanPromotionalBannerProps> = ({
  bannerId = '',
  className,
  showCloseButton = false,
  promotionalHeaderMessage,
  promotionalSubheaderMessage,
}) => {
  const isFeatureFlagEnabled = useEnableUpgradePlanPromotionalBanner();
  const { billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) || {};
  const hasFreemiumPlan = billingPlanId === BillingPlanIds.Free2025Q2;

  const [showsUpgradeBanner] = useCountryConfig(CountryConfigNames.ShowsUpgradePlanPromotionalBanner);
  const shouldShowBanner = hasFreemiumPlan && showsUpgradeBanner;

  const { openManageSubscriptionDrawer } = useGlobalModalLaunchContext();
  const [hasDismissedBanner, setBannerDismissalStatus] = useUserSpecificLocalStorage(bannerId);

  const handleClose = useCallback(() => {
    if (bannerId) {
      setBannerDismissalStatus(true);
    }
  }, [bannerId, setBannerDismissalStatus]);

  const onClick = useCallback(() => {
    openManageSubscriptionDrawer();
  }, [openManageSubscriptionDrawer]);

  if (!isFeatureFlagEnabled || hasDismissedBanner || !shouldShowBanner) {
    return null;
  }

  return (
    <PromotionalBanner
      className={className}
      onClose={showCloseButton ? handleClose : undefined}
      promotionalHeaderMessage={promotionalHeaderMessage}
      promotionalSubheaderMessage={promotionalSubheaderMessage}
    >
      <Button onClick={onClick} theme={THEME_CONSTANTS.promotional}>
        <FormattedMessage {...messages.viewPlansButton} />
      </Button>
    </PromotionalBanner>
  );
};
